import { getDocument, GlobalWorkerOptions, RenderTask } from 'pdfjs-dist';
import { PDFDocumentProxy } from 'pdfjs-dist/types/src/display/api';
import { RefObject, use, useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { isSongFileMissing, SongFileExtension } from '../../../common/src/model.ts';
import { SypMusicFile } from '../../../common/src/syp_file.ts';
import BackArrowIcon from '../assets/arrow-previous-left-icon.svg?react';
import KebabIcon from '../assets/ellipsis-vertical.svg?react';
import { useUserAttributes } from '../data/use_user_attributes.ts';
import { RequestSongPage } from '../pages/request_song_page.tsx';
import { swipeDurationMs } from '../player/player_util.tsx';
import { SequencerContext } from '../player/sequencer.tsx';
import {
  isReloadDeferredByPlayback,
  isSongReloadHeld,
  noteSongReloadDeferred,
  shouldReloadForHashChange,
  SongFileVersion,
} from '../player/song_reload.ts';
import { useSongReloadPulse } from '../player/use_song_reload_pulse.ts';
import { Solfege } from './music_renderer.tsx';
import { getPdfUrlForHymn, isCustomSong } from './path.ts';
import { DownArrow, FloatingBackButton, Title } from './shared.tsx';
import { useDebounce } from './use_debounce.tsx';

type PdfLoadVersion = SongFileVersion & { isMissing: boolean };

interface Props {
  onClose?: () => void;
  isAudioMissing?: boolean; // missing MIDI or vocals
  areNavigationButtonsVisible?: boolean;
  isInSongSelector?: boolean;
  onMinimize?: (event: any) => void;
  onKebabClick?: (event: any) => void;
  sypFile?: SypMusicFile;
  setIsPdfScrolledToTop?: (isScrolledToTop: boolean) => void;
}

export function PdfViewer({
  isAudioMissing,
  onClose,
  areNavigationButtonsVisible,
  isInSongSelector,
  onMinimize,
  onKebabClick,
  sypFile,
  setIsPdfScrolledToTop,
}: Props) {
  const { copyrightIssues, hymn } = use(SequencerContext);
  // The pdf's url does not change when the cache refresher replaces the file, so the song's
  // manifest content hash comes along to drive a reload (issue #3517 item B1). The hymn itself is
  // what gets debounced - its identity is stable across manifest refreshes (see hymn_identity.ts)
  // and changes only when the song or its content changed - so the url and the hash can never
  // briefly disagree and cause a double load.
  const debouncedHymn = useDebounce(hymn, 100);
  const pdfUrl = debouncedHymn && getPdfUrlForHymn(debouncedHymn);
  const pdfHash = debouncedHymn?.hash;
  // Taken from the debounced hymn, so it can never disagree with the url and the hash above: the
  // manifest says which files the song has, and a file it does not have is not worth asking for
  // (issue #3265).
  const isPdfMissing = !!debouncedHymn && isSongFileMissing(debouncedHymn, SongFileExtension.Pdf);
  const { isInternalUser } = useUserAttributes();
  const [isSolfegeVisible, setIsSolfegeVisible] = useState(false);
  const pdfWrapperRef = useRef<HTMLDivElement>(null);
  const [errorLoadingPdf, setErrorLoadingPdf] = useState(false);
  const isPdfVisible = !copyrightIssues || isInternalUser;

  useEffect(() => {
    if (!setIsPdfScrolledToTop) {
      return;
    }
    let atTopTimeout: ReturnType<typeof setTimeout> | undefined;
    const listener = () => {
      const atTop = (pdfWrapperRef.current?.scrollTop ?? 0) <= 0;
      if (atTop) {
        // wait to avoid minimizing when scrolling back to top
        if (atTopTimeout !== undefined) clearTimeout(atTopTimeout);
        atTopTimeout = setTimeout(() => setIsPdfScrolledToTop(atTop), swipeDurationMs + 100);
      } else {
        setIsPdfScrolledToTop(atTop);
      }
    };
    const { current } = pdfWrapperRef;
    current?.addEventListener('scroll', listener);
    return () => {
      if (atTopTimeout !== undefined) clearTimeout(atTopTimeout);
      current?.removeEventListener('scroll', listener);
    };
  }, [setIsPdfScrolledToTop]);

  const isRequestPageVisible = !isInSongSelector && (errorLoadingPdf || (!isInternalUser && !!copyrightIssues));

  const sheetMusic =
    isSolfegeVisible && sypFile ? (
      <Solfege sypFile={sypFile} />
    ) : (
      <PdfWrapper key="pdf" ref={pdfWrapperRef}>
        {isRequestPageVisible && <RequestSongPage isMidiFileMissing={isAudioMissing} isDialog={true} />}
        <PdfMultiPageViewer
          isPdfVisible={isPdfVisible}
          pdfUrl={pdfUrl}
          pdfHash={pdfHash}
          isPdfMissing={isPdfMissing}
          scrollElementRef={pdfWrapperRef}
          onMinimize={onMinimize}
          onKebabClick={onKebabClick}
          setErrorLoadingPdf={setErrorLoadingPdf}
        />
      </PdfWrapper>
    );

  const onSolfegeToggle = useCallback(() => {
    setIsSolfegeVisible(!isSolfegeVisible);
  }, [isSolfegeVisible]);

  return areNavigationButtonsVisible ? (
    <>
      <FloatingBackButton onClick={onClose}>
        <BackArrowIcon />
      </FloatingBackButton>
      {isInternalUser && sypFile && (
        <FloatingSolfegeButton onClick={onSolfegeToggle}>
          {isSolfegeVisible ? 'Lyrics' : 'Solfège'}
        </FloatingSolfegeButton>
      )}
      {sheetMusic}
    </>
  ) : (
    sheetMusic
  );
}
export const PdfWrapper = styled.div`
  height: 100%;
  overflow-y: scroll;
  overflow-x: clip;
  background-color: var(--color-pdf-background);
  flex: 1;
`;

const FloatingSolfegeButton = styled.div`
  position: fixed;
  top: 0;
  right: 0;
  z-index: 1000;
  background-color: color-mix(in srgb, var(--color-pdf-background) 80%, transparent);
  color: var(--color-pdf-text);
  margin: 10px 18px;
  padding: 5px 10px;
  border-radius: 8px;
  cursor: pointer;
  font-family: Jost-SemiBold, Arial, sans-serif;
  font-size: 1.2em;
  border: 2px solid var(--color-text);
`;

export function initializePDFWorker() {
  const workerSrc = new URL('pdfjs-dist/legacy/build/pdf.worker.min.mjs', import.meta.url).toString();
  console.log(`PDF worker URL: ${workerSrc}`);
  GlobalWorkerOptions.workerSrc = workerSrc;
}

// An experimental viewer generated by ChatGPT (https://chatgpt.com/share/6916184d-9a60-8011-9782-4debd680809c)
// Advantages:
// - Surfaces abort controller for quick abort (works MUCH better with song selector)
// Disadvantages:
// - Currently only single page (surely we could chain canvases for multiple)
// - scale must be set manually
// TODO(hewitt): render multiple pages (ChatGPT https://chatgpt.com/share/6916184d-9a60-8011-9782-4debd680809c)
// TODO(hewitt): use native PDF renderer (branch hewitt/native-pdf-viewer - just need to make it work on iOS)

function PdfMultiPageViewer({
  isPdfVisible,
  pdfUrl,
  pdfHash,
  isPdfMissing,
  scrollElementRef,
  onMinimize,
  onKebabClick,
  setErrorLoadingPdf,
}: {
  isPdfVisible: boolean;
  pdfUrl: string | undefined;
  pdfHash?: string;
  isPdfMissing?: boolean;
  scrollElementRef?: RefObject<HTMLDivElement | null>;
  onMinimize?: (event: any) => void;
  onKebabClick?: (event: any) => void;
  setErrorLoadingPdf: React.Dispatch<React.SetStateAction<boolean>>;
}) {
  const [pdf, setPdf] = useState<PDFDocumentProxy | undefined>(undefined);
  const { hymn } = use(SequencerContext);

  // What the load effect below is keyed on. The hash cannot go straight into the effect's deps:
  // re-running the effect tears the loaded document down (its cleanup destroys it), so the
  // decision to reload has to be made before the effect runs. A hash change while the song is
  // playing leaves the target - and therefore the rendered document - untouched (#3517 item B1).
  // The effect is keyed on this whole state object, whose identity advances on every wanted
  // reload: keying it on the url alone meant a same-song hash change updated the state and never
  // reloaded the document.
  const loadTarget = isPdfVisible ? pdfUrl : undefined;
  // The missing flag rides on the version rather than feeding the load effect directly, so it is
  // gated exactly as the hash is: a manifest change under a playing song changes neither until the
  // gate opens (a flag flip without a hash change waits for the next hash change, which in
  // practice is the same manifest write).
  const nextVersion: PdfLoadVersion | undefined = loadTarget
    ? { slug: loadTarget, hash: pdfHash, isMissing: isPdfMissing ?? false }
    : undefined;
  const [loadVersion, setLoadVersion] = useState<PdfLoadVersion | undefined>(nextVersion);
  const restoreScrollTopRef = useRef<number | undefined>(undefined);
  // A hash change while the song is playing leaves the rendered document alone. Reading the pulse
  // re-renders this component when the update gate releases that deferred reload, so the decision
  // below is taken again with playback stopped and the scroll-restoring reload runs (issue #3265).
  useSongReloadPulse();
  const versions = {
    previous: loadVersion,
    next: nextVersion,
    isHeld: isSongReloadHeld(),
  };
  const action = shouldReloadForHashChange(versions);
  // Noted from an effect, not during render: the gate reads its own clock, and a render that
  // straddled the idle boundary could otherwise have the gate flush synchronously mid-render.
  const isDeferred = action === 'none' && isReloadDeferredByPlayback(versions);
  useEffect(() => {
    if (isDeferred) {
      noteSongReloadDeferred();
    }
  }, [isDeferred]);
  if (action !== 'none') {
    // A same-url reload replaces the pages under the reader, so put them back where they were.
    // Any pending restore is dropped for a new song: it belongs to the document being replaced,
    // and a new song starts at the top.
    restoreScrollTopRef.current =
      action === 'same-song-refresh' ? (scrollElementRef?.current?.scrollTop ?? undefined) : undefined;
    setLoadVersion(nextVersion);
  }

  // Restore the scroll position of a reloaded document once its pages have grown back: each page
  // sizes its canvas asynchronously, so keep reapplying until the content is tall enough.
  useEffect(() => {
    const scrollTop = restoreScrollTopRef.current;
    const element = scrollElementRef?.current;
    if (!pdf || !scrollTop || !element) {
      return;
    }
    restoreScrollTopRef.current = undefined;
    let attempts = 0;
    let frame = requestAnimationFrame(function restore() {
      element.scrollTop = scrollTop;
      attempts += 1;
      if (element.scrollTop < scrollTop && attempts < 120) {
        frame = requestAnimationFrame(restore);
      }
    });
    return () => cancelAnimationFrame(frame);
  }, [pdf, scrollElementRef]);

  useEffect(() => {
    const loadUrl = loadVersion?.slug;
    // eslint-disable-next-line
    setPdf(undefined);
    setErrorLoadingPdf(false);
    if (!loadUrl) {
      return;
    }
    if (loadVersion?.isMissing) {
      // The manifest says this song has no .pdf: show the same thing a failed load shows (the
      // request-song page), without the request (issue #3265). When the file is generated the
      // song's hash changes, loadVersion advances and the load runs normally.
      setErrorLoadingPdf(true);
      return;
    }

    const abortController = new AbortController();
    const signal = abortController.signal;

    let localPdf: PDFDocumentProxy | undefined = undefined;
    const pdfContext = `url=${loadUrl} protocol=${window.location.protocol} ua=${navigator.userAgent}`;

    const stallTimeout = setTimeout(() => {
      if (!signal.aborted) {
        console.error(`PDF load stalled after 15s: ${pdfContext}`);
        setErrorLoadingPdf(true);
        setPdf(undefined);
        abortController.abort();
      }
    }, 15_000);

    void (async () => {
      try {
        localPdf = await getDocument({
          url: loadUrl,
          // @ts-expect-error TS does not recognize this function signature
          signal,
          disableRange: true,
          disableStream: true,
          disableAutoFetch: true,
        }).promise;
        clearTimeout(stallTimeout);
        if (signal.aborted) {
          void localPdf.destroy();
          return;
        }
        setPdf(localPdf);
      } catch (err: any) {
        clearTimeout(stallTimeout);
        setErrorLoadingPdf(true);
        setPdf(undefined);
        console.log(`PDF load error (${err.name}): ${err.message} | ${pdfContext}`);
        abortController.abort();
        return;
      }
    })();

    return () => {
      clearTimeout(stallTimeout);
      setPdf(undefined);
      abortController.abort(); // cancels load + render
      void localPdf?.destroy();
    };
  }, [loadVersion, setErrorLoadingPdf]);

  const header = onMinimize && onKebabClick && (
    <PdfHeader>
      <MinimizeButton onClick={onMinimize} />{' '}
      <SongTitle $lineClamp={1}>
        {hymn ? (isCustomSong(hymn.slug) ? '' : hymn.songNumber + ' - ') + hymn.title : ''}
      </SongTitle>
      <KebabButton id={songKebabButtonId} onClick={onKebabClick}>
        <Kebab />
      </KebabButton>
    </PdfHeader>
  );

  return (
    <PdfPageStacker>
      {header}
      {isPdfVisible &&
        pdf &&
        [...Array(pdf.numPages).keys()].map((pageNumber) => (
          <PdfSinglePageViewer key={pageNumber} document={pdf} pageNumber={pageNumber + 1} />
        ))}
    </PdfPageStacker>
  );
}

export const songKebabButtonId = 'song-kebab-button';

export function PdfSinglePageViewer({ document, pageNumber }: { document: PDFDocumentProxy; pageNumber: number }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!document || !canvas || !ctx) {
      return;
    }

    const abortController = new AbortController();
    const renderTaskWrapper: { renderTask?: RenderTask } = {};

    void (async () => {
      let pageObj;
      try {
        pageObj = await document.getPage(pageNumber);
      } catch {
        return; // document was destroyed (e.g. song changed)
      }

      if (abortController.signal.aborted) {
        return;
      }

      const scale = 3; // affects crispness of image (1 blurry, 5 super crisp on large screen)
      const viewport = pageObj.getViewport({ scale });

      canvas.width = viewport.width;
      canvas.height = viewport.height;

      renderTaskWrapper.renderTask = pageObj.render({
        canvasContext: ctx,
        viewport,
      });
    })();

    return () => {
      abortController.abort();
      renderTaskWrapper.renderTask?.cancel();
    };
  }, [document, pageNumber]);

  return <PdfCanvas ref={canvasRef} style={{}} />;
}

// A thin strip of page colour down each side: many PDFs are cropped right at the music (by whoever
// made them, or by crop_pdfs.ts, which leaves only a few points), and a system's left barline
// otherwise sits on the very edge of a phone screen.
const pdfSideGutter = '1.5vw';

const PdfCanvas = styled.canvas`
  width: calc(100vw - 2 * ${pdfSideGutter});
  margin: 0 ${pdfSideGutter};
  filter: var(--pdf-dark-mode-filter);
`;

const PdfPageStacker = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
`;

export const PdfHeaderHeight = 'max(min(3.5vw, 3.5vh), 30px)';

const PdfHeader = styled.div`
  position: relative;
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: ${PdfHeaderHeight};
`;

export const pdfMinimizeButtonId = 'pdf-minimize-button';

function MinimizeButton({ onClick }: { onClick: (event: any) => void }) {
  return (
    <MinimizeButtonWrapper id={pdfMinimizeButtonId} onClick={onClick}>
      <DownArrow $color={'var(--color-pdf-text)'} />
    </MinimizeButtonWrapper>
  );
}

const outerPdfHeaderHorizontalMargin = '2vw';

const MinimizeButtonWrapper = styled.div`
  flex-shrink: 0;
  margin-left: ${outerPdfHeaderHorizontalMargin};
`;

const KebabButton = styled.div`
  flex-shrink: 0;
  margin-right: ${outerPdfHeaderHorizontalMargin};
`;

const Kebab = styled(KebabIcon)`
  transform: rotate(90deg);
  width: 30px;
  height: 30px;
  cursor: pointer;
  fill: var(--color-pdf-text);
`;

const SongTitle = styled(Title)`
  overflow: hidden;
  margin: 0 10px;
  font-size: 1rem;
  color: var(--color-pdf-text);
`;
